import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Howl } from 'howler';

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function SongCard({ song, index }) {
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const howlRef = useRef(null);
  const animRef = useRef(null);
  const PREVIEW_DURATION = 20;

  const handlePreview = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPreviewing) {
      stopPreview();
      return;
    }

    startPreview();
  };

  const startPreview = () => {
    if (howlRef.current) {
      howlRef.current.unload();
    }

    const apiBase = process.env.REACT_APP_API_URL || '';
    const audioUrl = `${apiBase}${song.audioUrl}`;

    const howl = new Howl({
      src: [audioUrl],
      html5: true,
      volume: 0.7,
      onplay: () => {
        setIsPreviewing(true);
        const start = howl.seek();
        const trackProgress = () => {
          const current = howl.seek() - start;
          const progress = (current / PREVIEW_DURATION) * 100;
          setPreviewProgress(Math.min(100, progress));

          if (current >= PREVIEW_DURATION) {
            stopPreview();
            return;
          }

          animRef.current = requestAnimationFrame(trackProgress);
        };
        animRef.current = requestAnimationFrame(trackProgress);
      },
      onstop: () => { setIsPreviewing(false); setPreviewProgress(0); },
      onend: () => { setIsPreviewing(false); setPreviewProgress(0); },
    });

    howl.seek(song.previewStart || 0);
    howl.play();
    howlRef.current = howl;
  };

  const stopPreview = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    howlRef.current?.stop();
    setIsPreviewing(false);
    setPreviewProgress(0);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      howlRef.current?.unload();
    };
  }, []);

  const diffClass = {
    easy: 'diff-easy',
    medium: 'diff-medium',
    hard: 'diff-hard',
  }[song.difficulty] || 'diff-medium';

  return (
    <Link
      to={`/sing/${song.id}`}
      className="song-card"
      style={{ animationDelay: `${(index % 8) * 0.05}s` }}
      onClick={stopPreview}
    >
      {/* Cover art */}
      <div className="song-cover">
        {song.coverArt ? (
          <img src={`${process.env.REACT_APP_API_URL || ''}${song.coverArt}`} alt={song.title} />
        ) : (
          <div className="song-cover-placeholder">🎵</div>
        )}

        {/* Preview button */}
        <button
          className="song-preview-btn"
          onClick={handlePreview}
          title={isPreviewing ? 'Stop preview' : 'Preview 20s'}
        >
          {isPreviewing ? '⏹' : '▶'}
        </button>

        {/* Preview progress bar */}
        {isPreviewing && (
          <div className="preview-bar">
            <div className="preview-bar-fill" style={{ width: `${previewProgress}%` }} />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="song-card-body">
        <div className="song-title">{song.title}</div>
        <div className="song-artist">{song.artist}</div>

        <div className="song-meta">
          {song.duration && (
            <span className="song-meta-item">⏱ {formatDuration(song.duration)}</span>
          )}
          {song.bpm && (
            <span className="song-meta-item">♩ {Math.round(song.bpm)} BPM</span>
          )}
          {song.musicalKey && (
            <span className="song-meta-item">🎼 {song.musicalKey}</span>
          )}
          {song.viewCount > 0 && (
            <span className="song-meta-item">👁 {song.viewCount.toLocaleString()}</span>
          )}
        </div>

        <div className="song-actions">
          <span className={`badge ${diffClass}`} style={{ padding: '3px 10px', borderRadius: 999 }}>
            {song.difficulty}
          </span>
          {song.hasLyrics && (
            <span className="badge badge-purple">📝 Lyrics</span>
          )}
          {song.genre && (
            <span className="badge badge-fuchsia">{song.genre}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
